import type { MigrationDeployment } from '@schema';
import {
  DeploymentStatus,
  DEPLOYMENT_STATUS_TEXT,
  DEPLOYMENT_STATUS_ICON
} from '@/common/constants/deployment.constants';
import { dateFormatter } from '@/common/utils/formatter.date';
import type { MappedProgressUpdate } from '@/common/utils/taskMapper';
import {
  useMigrationTasksStore
} from '@/common/store/pinia/migrationTasks';
import type { MappedMigrationTask } from '@/common/store/pinia/migrationTasks/migrationTasksMapper';
import {
  mapInstance,
  mapSourceOptions,
  mapTargetOptions,
  mapTargetMappings,
  type MigrationSourceVM,
  type MigrationSourceOptions,
  type MigrationTargetOptions,
  type MigrationTargetMapping
} from '@/common/store/pinia/migrations/migrationsMapper';

export type { MappedProgressUpdate as DeploymentProgressUpdate };
export type MappedDeploymentTask = MappedMigrationTask;

export interface MigrationTargetInstance {
  uuid: string;
  name: string;
}

export const mapTargetInstances = (
  targetInstances: MigrationDeployment['target_instances']
): MigrationTargetInstance[] => (targetInstances ?? []).map(t => ({
  uuid: t.uuid ?? '',
  name: t.name ?? ''
}));

export interface MappedDeployment {
  id: string;
  migrationId: string;
  name: string;
  date: string;
  status: DeploymentStatus;
  dateFormatted: string;
  statusText: string;
  statusIcon: string;
  tasks: MappedDeploymentTask[];
  deploymentOpts: {
    cloneDisks: boolean;
    forceDeployment: boolean;
    osMorphing: boolean;
    saveSnapshot: boolean;
    start: boolean;
  };
  sourceCloudId: string;
  createdAt: string;
  updatedAt: string;
  sourceOptions: MigrationSourceOptions;
  targetOptions: MigrationTargetOptions;
  sourceVMs: MigrationSourceVM[];
  targetMappings: MigrationTargetMapping;
  targetInstances: MigrationTargetInstance[];
}

const mapper = (d: MigrationDeployment): MappedDeployment => {
  const status = (d.status as DeploymentStatus) ?? DeploymentStatus.UNEXECUTED;
  const date = d.updated_at ?? d.created_at ?? '';
  const instances = d.instances ?? [];
  const id = d.id ?? '';
  return {
    id,
    migrationId: d.migration_id ?? '',
    name: d.name ?? '',
    date,
    status,
    dateFormatted: dateFormatter(date),
    statusText: DEPLOYMENT_STATUS_TEXT[status],
    statusIcon: DEPLOYMENT_STATUS_ICON[status],
    tasks: useMigrationTasksStore().getMappedTasksByParent(id),
    deploymentOpts: {
      cloneDisks: d.deployment_options?.clone_disks ?? false,
      forceDeployment: d.deployment_options?.force_deployment ?? false,
      osMorphing: d.deployment_options?.os_morphing ?? false,
      saveSnapshot: d.deployment_options?.save_snapshot ?? false,
      start: d.deployment_options?.start ?? false
    },
    sourceCloudId: d.source_cloud_id ?? '',
    createdAt: d.created_at ?? '',
    updatedAt: d.updated_at ?? '',
    sourceOptions: mapSourceOptions(instances, d.source_environment),
    targetOptions: mapTargetOptions(d.target_environment),
    sourceVMs: instances.map(mapInstance),
    targetMappings: mapTargetMappings(d.network_mapping, d.storage_mapping),
    targetInstances: mapTargetInstances(d.target_instances)
  };
};

export default mapper;
